// Context Manager - Handles thread/forecast context initialization and URL parameter parsing
import axios from 'axios';
import { getCurrentModeConfig, CHAT_MODES } from './modeConfigs';

// Modes removed: always use single default mode; layout is controlled separately

/**
 * Fetch workspace thread and forecasts in parallel
 * This is the primary initialization method that satisfies the requirement:
 * "fetch both thread and forecast in parallel, then fetch messages and components"
 */
export const fetchWorkspaceContext = async (workspaceId) => {
  try {
    const [threadData, forecastsData] = await Promise.all([
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/get-thread/${workspaceId}`,
        { withCredentials: true }
      ).catch(() => ({ data: null })),
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/get-forecasts/${workspaceId}`,
        { withCredentials: true }
      ).catch(() => ({ data: [] }))
    ]);

    const thread = threadData.data || null;
    const forecasts = Array.isArray(forecastsData.data) ? forecastsData.data : [];
    const forecast = forecasts[0] || null;

    return { thread, forecast, forecasts };
  } catch (error) {
    console.error('Error fetching workspace context:', error);
    return { thread: null, forecast: null, forecasts: [] };
  }
};

// Legacy individual fetch functions (kept for backwards compatibility if needed)
export const fetchWorkspaceThread = async (workspaceId) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/get-thread/${workspaceId}`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching workspace thread:', error);
    return null;
  }
};

export const fetchWorkspaceForecasts = async (workspaceId) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/workspaces/get-forecasts/${workspaceId}`,
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching workspace forecasts:', error);
    return [];
  }
};

// Disallowed: '/api/forecasts/get-forecast' removed. Forecast metadata must come from navigation state or workspace list

/**
 * Load forecast components
 * Fetches compiled cash_json format and transforms to frontend format
 * @param {string} forecastId - The forecast ID to load
 * @param {Function} setComponents - Function to set components state
 * @param {Function} setComponentsFetched - Function to set fetched flag
 * @param {Function} setComponentsLoading - Function to set loading state
 * @param {Function} [setForecast] - Optional function to update forecast metadata (calendar, etc.)
 */
export const loadForecastComponents = async (forecastId, setComponents, setComponentsFetched, setComponentsLoading, setForecast = null, user, workspace) => {
  try {
    if (typeof setComponentsLoading === 'function') setComponentsLoading(true);
    
    // Import the transformer
    const { transformCashJsonToFrontend } = await import('../../../components/Forecast/utils/jsonTransformer');
    
    // Fetch the compiled forecast in cash_json format
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/compile/${forecastId}/${user?.id}/${workspace?.id}`,
      { withCredentials: true }
    );
    
    if (response.data?.success && response.data?.cashJson) {
      // console.log('Loaded compiled cash_json:', response.data.cashJson);
      
      // Transform cash_json to frontend format
      const cashJsonData = response.data.cashJson;
      const { forecast: transformedForecast, components: transformedComponents } = transformCashJsonToFrontend(cashJsonData, {
        calculateFromFormulas: false  // Use pre-computed cache values from backend (backend handles all formula evaluation)
      });
      
      // console.log('Transformed forecast & components:', { forecast: transformedForecast, components: transformedComponents });
      
      // Update forecast metadata if setter is provided (includes calendar, etc.)
      // IMPORTANT: Preserve status and build_steps from original forecast metadata
      if (setForecast && transformedForecast) {
        setForecast(prevForecast => ({
          ...prevForecast,
          ...transformedForecast,
          // Preserve critical metadata (use transformed values as fallback if prevForecast doesn't have them)
          status: prevForecast?.status || transformedForecast.status,  // Preserve actual status (in-progress, completed, etc.)
          build_steps: prevForecast?.build_steps || transformedForecast.build_steps  // Preserve build step tracking
        }));
      }
      
      setComponents(transformedComponents || []);
    } else {
      console.warn('No cash_json data returned for forecast:', forecastId);
      setComponents([]);
    }
  } catch (error) {
    console.error('Error loading forecast components:', error);
    setComponents([]);
  } finally {
    setComponentsFetched(true);
    if (typeof setComponentsLoading === 'function') setComponentsLoading(false);
  }
};

/**
 * Get messages for a thread
 */
export const getMessages = async (threadId, setMessages, setLoading, setShowGreeting, setMessagesLoading) => {
  try {
    if (typeof setMessagesLoading === 'function') setMessagesLoading(true);
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/threads/get-messages/${threadId}`,
      { withCredentials: true }
    );
    let fetchedMessages = response.data;
    if (!Array.isArray(fetchedMessages)) {
      fetchedMessages = [];
    }
    // Filter out messages with blank or non-existent content
    const filteredMessages = fetchedMessages.filter(message => 
      message.content !== undefined && 
      message.content !== null && 
      message.content.trim() !== ''
    );
    setMessages(filteredMessages);
    return filteredMessages;
  } catch (error) {
    console.error('Error fetching thread messages.');
    setShowGreeting(true);
    return [];
  } finally {
    if (typeof setMessagesLoading === 'function') setMessagesLoading(false);
    if (typeof setLoading === 'function') setLoading(false);
  }
};

export const clearThread = async (threadId) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/threads/clear-thread/${threadId}`,
      {},
      { withCredentials: true }
    );
    return true;
  } catch (error) {
    console.error('Error clearing thread messages:', error);
    return false;
  }
};

/**
 * Initialize context from thread ID
 */
export const initFromThread = async ({
  threadId,
  lastContextRef,
  orchestrateSwitch,
  setChatMode,
  layoutView,
  setLayoutView,
  setThread,
  setShowGreeting,
  getMessages,
  setMessagesLoading,
  user,
  workspace
}) => {
  if (lastContextRef.current.threadId === threadId) return;
  
  await orchestrateSwitch({
    loadFn: async () => {
      // Use only allowed endpoints; we cannot fetch thread meta by id
      setChatMode && setChatMode('general');
      const targetLayout = 'full';
      if (targetLayout !== layoutView) setLayoutView(targetLayout);

      setThread({ id: threadId });
      await getMessages(threadId);
      lastContextRef.current = {
        threadId,
        forecastId: null,
        mode: 'general'
      };
    }
  });
};

/**
 * Initialize context from forecast ID
 */
export const initFromForecast = async ({
  forecastId,
  routeForecastId,
  lastContextRef,
  orchestrateSwitch,
  location,
  // New: allow immediate mode/layout adjustments without clearing
  setChatMode,
  layoutView,
  setLayoutView,
  setForecast,
  loadForecastComponents,
  setThread,
  navigate,
  getMessages,
  setShowGreeting,
  setMessagesLoading,
  user,
  workspace
}) => {
  // If this forecast is already the active context, avoid re-orchestrating (prevents fade flicker)
  if (lastContextRef.current.forecastId === forecastId) return;
  
  await orchestrateSwitch({
    loadFn: async () => {
      // Prevent greeting flicker while loading
      setShowGreeting(false);

      // Prefer navigation state if present
      let fetched = null;
      const navForecast = location.state?.forecast;
      if (navForecast && navForecast.id === forecastId) {
        fetched = navForecast;
        setForecast(navForecast);
        // Force default mode and canvas layout when navigating with a forecast
        setChatMode && setChatMode('general');
        if (layoutView !== 'canvas') setLayoutView('canvas');
      } else {
        // No direct forecast lookup allowed; initialize minimal forecast context
        fetched = { id: forecastId };
        setForecast(fetched);
      }

      // Always use default mode
      let desiredMode = 'general';

      // Load components now that forecast meta is available
      if (fetched && fetched.id) {
        try {
          await loadForecastComponents(forecastId, null, null, null, setForecast, user, workspace);
        } catch (e) {
          console.error('Failed to load forecast components for forecast:', forecastId, e);
        }
      }

      if (fetched?.threadId) {
        setThread({ id: fetched.threadId });
        // Apply default mode and layout (canvas when forecast exists)
        setChatMode && setChatMode('general');
        const targetLayout = 'canvas';
        if (targetLayout !== layoutView) setLayoutView(targetLayout);
        if (typeof setMessagesLoading === 'function') setMessagesLoading(true);
        await getMessages(fetched.threadId);
        setShowGreeting(false);
        lastContextRef.current = { threadId: fetched.threadId, forecastId };
      } else {
        // Apply resolved mode even without a thread (layout/forecast-only view)
        setChatMode('general');
        const targetLayout = 'canvas';
        if (targetLayout !== layoutView) setLayoutView(targetLayout);
        setShowGreeting(false);
        lastContextRef.current = { threadId: null, forecastId };
      }
    }
  });
};

/**
 * Main context initialization handler
 * Handles both /threads and /forecasts/:forecastId routes
 */
export const initializeContext = async ({
  location,
  routeForecastId,
  lastContextRef,
  thinking,
  thread,
  messages,
  forecast,
  components,
  orchestrateSwitch,
  user,
  workspace,
  // All required setters and functions
  setChatMode,
  layoutView,
  setLayoutView,
  setThread,
  setShowGreeting,
  setForecast,
  setComponents,
  setComponentsFetched,
  setComponentsLoading,
  setMessages,
  setLoading,
  setMessagesLoading,
  navigate
}) => {
  if (thinking) return;

  // URL no longer carries a thread identifier
  const presetMode = location.state?.presetMode;

  // If user arrived intending to start a fresh thread with a preset mode, apply it
  if (presetMode && CHAT_MODES[presetMode]) {
    await orchestrateSwitch({
      loadFn: async () => {
        // Reset context but keep greeting visible
        lastContextRef.current = { threadId: null, forecastId: null };
        const targetLayout = getCurrentModeConfig('general').layoutView;
        if (targetLayout !== layoutView) setLayoutView(targetLayout);
        setShowGreeting(true);
      }
    });
    // Clear navigation state to avoid re-triggering on subsequent renders, but force a new key so changing mode re-applies
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: { __cleared: true } });
    return;
  }

  // Create bound helper functions that have access to all state setters
  const boundLoadForecastComponents = (forecastId) => loadForecastComponents(forecastId, setComponents, setComponentsFetched, setComponentsLoading, setForecast, user, workspace);
  const boundGetMessages = (threadId) => getMessages(threadId, setMessages, setLoading, setShowGreeting, setMessagesLoading);

  // Prioritize forecast route handling even if a thread param is present,
  // so mode/layout derives from the forecast context first.
  if (routeForecastId) {
    await initFromForecast({
      forecastId: routeForecastId,
      routeForecastId,
      lastContextRef,
      orchestrateSwitch,
      location,
      setChatMode,
      layoutView,
      setLayoutView,
      setForecast,
      loadForecastComponents: boundLoadForecastComponents,
      setThread,
      navigate,
      getMessages: boundGetMessages,
      setShowGreeting,
      user,
      workspace
    });
    return;
  }

  // Default /threads without specific context: orchestrate a clean reset if coming from any context
  if (
    lastContextRef.current.threadId !== null ||
    lastContextRef.current.forecastId !== null ||
    thread !== null ||
    (messages && messages.length > 0) ||
    forecast !== null ||
    (components && components.length > 0)
  ) {
    orchestrateSwitch({
      // Preserve current mode; do not force-switch to general
      nextMode: undefined,
      loadFn: async () => {
        lastContextRef.current = { threadId: null, forecastId: null };
        setShowGreeting(true);
      }
    });
  } else {
    lastContextRef.current = { threadId: null, forecastId: null };
    // Do not flip loading here; let the caller decide based on whether a thread exists
    // and only set loading to false after messages are retrieved or definitively none
    if (typeof setShowGreeting === 'function') setShowGreeting(false);
  }
};
